/**
 * RunRecordBody — the run record itself: Overview / Decisions / Evidence / Result.
 *
 * Extracted verbatim from `routes/workflows.runs.$instanceId.tsx` so there is
 * ONE implementation of "the run, explained" rather than two. The route keeps
 * its loader and mounts this; a later commit mounts the SAME component inside
 * the drawer's `MoreInformationSection`, which today re-assembles the same
 * material from `DrawerSectionMap` a second time.
 *
 * PRESENTATIONAL ONLY, and deliberately so — this is what lets two hosts share
 * it. No router, no loader, no fetch, no `useLoaderData`. Every formatted value
 * (durations, absolute times, the version string) is computed by whichever host
 * mounts it and handed in already-formatted. The one derivation that happens
 * here is `buildDecisionLines`, which is pure and must not be duplicated by
 * callers — both the Decisions and the Evidence section read from its output,
 * so deriving it once here is what keeps them consistent.
 *
 * `decisionsDegraded` and `decisionsPayload === null` are distinct signals and
 * both are load-bearing:
 *   - `decisionsPayload === null` tells `buildDecisionLines` to derive from
 *     `hitlEvents` alone (see decisionLines.ts's own contract note);
 *   - `decisionsDegraded` only drives the "showing what this run's own history
 *     can confirm" annotation on the Decisions heading.
 * A host that cannot call `get_run_decisions` at all passes `null` + `true`.
 */
import type { WarningAckState } from "../hooks/useWarningAcknowledgements";
import { useState, type ReactNode } from "react";
import ToggleButtonGroup from "~/common/components/ToggleButtonGroup";
import { DrawerSection } from "../drawer/DrawerSection";
import { buildDecisionLines } from "./decisionLines";
import { RunOverviewSection } from "./RunOverviewSection";
import { RunDecisionsSection } from "./RunDecisionsSection";
import { RunEvidenceSection } from "./RunEvidenceSection";
import type { RunEvidenceStepsInput } from "./RunEvidenceSection";
import type { PhaseManifest } from "../phaseModel";
import { RunResultSection } from "./RunResultSection";
import type { GetRunDecisionsResponse } from "./types";
import type { WorkflowStatus } from "~/features/workflows/monitor.types";
import type { WorkflowArtifact } from "../components/WorkflowArtifactsSection";
import type { HitlEvent } from "../components/WorkflowHitlHistorySection";
import type { WorkflowResultBody } from "../result.types";

export interface RunRecordBodyProps {
  workflowTitle: string | null;
  status: WorkflowStatus;
  hasWarnings?: boolean;
  /** Warning acknowledgement (2026-10-06): the pill's "Completed*" state. */
  needsAcknowledgement?: boolean | null;
  /** Warning acknowledgement: the host's live state for the Result section's lines. */
  warningAcknowledgements?: WarningAckState | null;
  clientName: string | null;
  policyNumber: string | null;
  triggeredByName: string | null;
  startedAtFormatted: string | null;
  endedAtFormatted: string | null;
  duration: string | null;
  currentStep: string | null;
  progress: string | null;
  workflowVersion: string | null;
  artifacts: WorkflowArtifact[];
  /** Whether the host is still fetching artifacts — drives the Evidence
   *  section's skeleton instead of an empty state that reads as an answer. */
  artifactsLoading?: boolean;
  hitlEvents: HitlEvent[];
  /** Run inputs for the chronological evidence view. A BUNDLE, not a derived
   *  chronology array, so `RunStepsLog` owns the manifest resolution for every
   *  host — see RunEvidenceSection on why the leaf component was not enough. */
  runSteps: RunEvidenceStepsInput;
  /** Declared phase/milestone manifest for the Servicer's collapsed
   *  chronology. See RunEvidenceSection on why it is separate from runSteps. */
  phaseManifest?: PhaseManifest | null;
  decisionsPayload: GetRunDecisionsResponse | null;
  decisionsDegraded: boolean;
  resultBody: WorkflowResultBody | null;
  /**
   * What is being waited ON, rendered ABOVE the record's own sections.
   *
   * Above, not inside, and deliberately: the four sections answer "what
   * happened", and this answers "what has to happen next" — a reader who
   * has to expand an accordion to discover the run is stuck has already been
   * failed. Absent prop reproduces the previous markup exactly, which is what
   * keeps the drawer's mount of this component unchanged (it has its own,
   * richer treatment of the same state and must not grow a second one).
   */
  waitBlock?: ReactNode;
  /** Host-specific blocks that belong INSIDE the record — the drawer's
   *  `CarrierHandoff` / `EndorsementHistory` / `DebugActionsPanel`. Unused by
   *  the route today; declared now because the drawer mount needs somewhere to
   *  put them that is not a second assembly. */
  extraBlocks?: ReactNode;
  /** What the record was launched FROM, rendered under All details. The
   *  Servicer already knows what they asked for; a reader reconstructing the
   *  run afterwards does not. */
  requestBlock?: ReactNode;
  /** Host-specific Result DETAIL — "Open Full Report", the oraFix summary
   *  link, the full result body. Content only: the record owns the Result
   *  accordion and its leading sentence, which is what stops the hosts each
   *  wrapping their own and rendering Result twice. Shown under All details. */
  resultDetail?: ReactNode;
  /** Page padding by default; the drawer overlay is a narrower column and
   *  supplies its own chrome, so it asks for the tighter inset. Presentation
   *  only — no section renders differently. */
  variant?: "page" | "drawer";
}

export type RunRecordView = "servicer" | "all";

/** The same bordered segment group the Monitor's window control uses — one
 *  more instance of a known control rather than a new vocabulary. */
const VIEW_OPTIONS: Array<{ id: string; label: string; value: RunRecordView }> = [
  { id: "run-record-view-servicer", label: "For the Servicer", value: "servicer" },
  { id: "run-record-view-all", label: "All details", value: "all" },
];

export function RunRecordBody({
  workflowTitle,
  status,
  hasWarnings = false,
  needsAcknowledgement = null,
  warningAcknowledgements = null,
  clientName,
  policyNumber,
  triggeredByName,
  startedAtFormatted,
  endedAtFormatted,
  duration,
  currentStep,
  progress,
  workflowVersion,
  artifacts,
  artifactsLoading = false,
  hitlEvents,
  runSteps,
  phaseManifest = null,
  decisionsPayload,
  decisionsDegraded,
  resultBody,
  waitBlock,
  extraBlocks,
  requestBlock,
  resultDetail,
  variant = "page",
}: RunRecordBodyProps) {
  const decisionLines = buildDecisionLines(hitlEvents, decisionsPayload);
  // One control over the whole record, not per-section. Defaults to the
  // Servicer view: the common case is someone asking "what happened to my
  // request", and the engineering detail is what they have to opt into.
  const [view, setView] = useState<RunRecordView>("servicer");
  const allDetails = view === "all";

  return (
    <div
      className={`flex flex-col gap-3 ${
        variant === "drawer" ? "p-3" : "px-6 py-5"
      }`}
      data-testid="run-record-page"
      data-view={view}
    >
      {waitBlock ?? null}

      <RunOverviewSection
        clientName={clientName}
        policyNumber={policyNumber}
        workflowTitle={workflowTitle}
        status={status}
        hasWarnings={hasWarnings}
        needsAcknowledgement={needsAcknowledgement}
        triggeredByName={triggeredByName}
        startedAtFormatted={startedAtFormatted}
        endedAtFormatted={endedAtFormatted}
        duration={duration}
        currentStep={currentStep}
        progress={allDetails ? progress : null}
        workflowVersion={allDetails ? workflowVersion : null}
        statusRowAdornment={
        <div data-testid="run-record-view-toggle">
          <ToggleButtonGroup
            id="run-record-view-togglebuttongroup"
            size="sm"
            selectedButton={
              VIEW_OPTIONS.find((o) => o.value === view)?.label ?? "For the Servicer"
            }
            buttonOptions={VIEW_OPTIONS.map((opt) => ({
              id: opt.id,
              label: opt.label,
              isActive: true,
            }))}
            onClick={(label) => {
              const next = VIEW_OPTIONS.find((o) => o.label === label)?.value;
              if (next) setView(next);
            }}
          />
        </div>
        }
      />

      {allDetails && requestBlock ? (
        <section aria-label="Submitted request" data-testid="run-record-request">
          {requestBlock}
        </section>
      ) : null}

      <section aria-label="Decisions" data-testid="run-record-decisions">
        <DrawerSection
          title="Decisions"
          defaultExpanded
          persistKey="run-record:decisions"
          rightAdornment={
            decisionsDegraded ? (
              <span
                className="text-2xs font-normal text-primary-gray-light-400"
                data-testid="run-record-decisions-degraded"
              >
                (showing what this run&apos;s own history can confirm)
              </span>
            ) : undefined
          }
        >
          <RunDecisionsSection lines={decisionLines} showProvenance={allDetails} />
        </DrawerSection>
      </section>

      <section aria-label="Evidence" data-testid="run-record-evidence">
        <DrawerSection
          title="Evidence"
          defaultExpanded
          persistKey="run-record:evidence"
        >
        <RunEvidenceSection
          artifacts={artifacts}
          artifactsLoading={artifactsLoading}
          decisionLines={decisionLines}
          runSteps={runSteps}
          hitlEvents={hitlEvents}
          allDetails={allDetails}
          phaseManifest={phaseManifest}
          showMilestoneSummary={variant === "page"}
        />
        </DrawerSection>
      </section>

      <section aria-label="Result" data-testid="run-record-result">
        <DrawerSection
          title="Result"
          defaultExpanded
          persistKey="run-record:result"
        >
          {/* The leading sentence and any warnings, always — and `always` has
              to include the DEFAULT view, which is where a servicer opening a
              bookmarked link from a HITL notification actually lands.
              `resultBody` is the only source of the `*_warning` strings
              (`extractResultWarnings`, read inside RunResultSection), so
              gating it on `allDetails` hid "issue the Service Summary manually
              in Epic" behind a toggle the reader has no reason to click —
              while BOTH host drawers show the same strings as a banner in
              every view. What "All details" gates is the raw body and the
              host's own report links (`resultDetail`, below), not the
              warning. */}
          <RunResultSection
            status={status}
            summary={decisionsPayload?.summary}
            result={resultBody}
            acknowledgements={warningAcknowledgements}
          />
          {allDetails && resultDetail ? (
            <div className="mt-2" data-testid="run-record-result-detail">
              {resultDetail}
            </div>
          ) : null}
        </DrawerSection>
      </section>

      {extraBlocks ?? null}
    </div>
  );
}

export default RunRecordBody;
